import React, { useState } from "react";
import { Plus } from "lucide-react";

/**
 * Thin horizontal spacer rendered BETWEEN two day blocks. It does two jobs:
 *   1. Drop target for day-reorder drag events (dataTransfer type
 *      "application/x-day-drag" — set by DayBlock's drag handle).
 *   2. On hover, reveals a "+ Añadir día aquí" button that inserts a new
 *      day at THIS position (i.e. right after the previous day).
 *
 * Props
 *   position:      the array index this gap sits BEFORE. gap position 0 =
 *                  before the first day. position N = after the last day.
 *   onDayDrop(pos) fires when a dragged day is dropped on this gap.
 *   onInsertBefore() fires when the user clicks the hover button.
 *   showInsertHere: hide the button (used for the very top gap so users
 *                  don't insert a "day zero"; only the drop target is kept).
 */
export function DayGap({ position, onDayDrop, onInsertBefore, showInsertHere = true }) {
  const [dragOver, setDragOver] = useState(false);
  const [hover, setHover] = useState(false);

  const handleDragOver = (e) => {
    // Only accept day drags, ignore service drags so the two flows don't
    // collide (services drop INSIDE DayBlock, days drop BETWEEN them).
    const types = Array.from(e.dataTransfer?.types || []);
    if (!types.includes("application/x-day-drag")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOver(true);
  };
  const handleDrop = (e) => {
    const types = Array.from(e.dataTransfer?.types || []);
    if (!types.includes("application/x-day-drag")) return;
    e.preventDefault();
    setDragOver(false);
    onDayDrop(position);
  };

  return (
    <div
      className="relative group"
      style={{ marginTop: -12, marginBottom: -12 }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onDragOver={handleDragOver}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      data-testid={`day-gap-${position}`}
    >
      {/* Persistent dashed hairline — signals the gap is interactive without
          waiting for a hover, and gives touch users a big enough target. */}
      <div
        className={`h-6 flex items-center justify-center transition-colors ${
          dragOver
            ? "border-t-2 border-terracotta bg-terracotta/5"
            : "border-t border-dashed border-clay-300/70 group-hover:border-terracotta/60"
        }`}
      />
      {showInsertHere && !dragOver && (
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-center pointer-events-none z-10">
          <button
            type="button"
            onClick={onInsertBefore}
            data-testid={`insert-day-at-${position}`}
            className={`pointer-events-auto inline-flex items-center gap-1 px-3 py-1 bg-white border text-[10px] uppercase tracking-widest transition-all shadow-sm ${
              hover
                ? "border-terracotta text-terracotta"
                : "border-clay-300 text-clay-500 hover:border-terracotta hover:text-terracotta"
            }`}
          >
            <Plus size={12}/> Añadir día aquí
          </button>
        </div>
      )}
    </div>
  );
}
